import { AxiosRequestConfig } from 'axios';
import configHelpers from 'helpers/config';

/**
 * The only place allowed to build an `Authorization: Bearer` header (see the
 * `no-restricted-syntax` rule in `.eslintrc`). A token is attached only when
 * the request host ends with one of `commonConfig.authAllowedDomains`, declared
 * per environment on the workload (production: in the `dr:` block too). A
 * missing key means no request carries a token (fail closed).
 */

const AUTHORIZATION = 'Authorization';

const toBearer = (token: string) => `Bearer ${token}`;

const normalizeDomain = (value: unknown) => {
  if (typeof value !== 'string') return null;
  const domain = value
    .trim()
    .toLowerCase()
    .replace(/^\*?\./, '');
  // A suffix is a bare host name: no scheme, port, path or wildcard, and at
  // least one dot
  if (!domain.includes('.') || /[/:*\s]/.test(domain)) return null;
  return domain;
};

// Same `customEvent` / action `error` shape as `nonBlockTrackError` in
// helpers/tracking. The tracker stub `window.track` is created by
// `new ReactTracker()` in App.tsx, which runs after this module loads, so the
// event is sent on the next tick
const reportConfigError = (errorCode: string, errorMessage: string) => {
  console.error(`[authHeader] ${errorMessage}`);
  setTimeout(() => {
    if (typeof window.track !== 'function') return;
    window.track('customEvent', 'authAllowedDomains', {
      action: 'error',
      category: 'staff-web-app',
      property: JSON.stringify({ name: errorCode, message: errorMessage }),
    });
  }, 0);
};

const parseAllowedDomains = (values: unknown) => {
  if (!Array.isArray(values)) {
    reportConfigError(
      'AUTH_ALLOWED_DOMAINS_MISSING',
      'commonConfig.authAllowedDomains is missing, no request will carry a token'
    );
    return [];
  }
  const domains = values
    .map(normalizeDomain)
    .filter((domain): domain is string => !!domain);
  const invalid = values.filter(value => !normalizeDomain(value));
  if (invalid.length) {
    reportConfigError(
      'AUTH_ALLOWED_DOMAINS_INVALID',
      `ignored invalid authAllowedDomains entries: ${invalid
        .map(value => JSON.stringify(value))
        .join(', ')}`
    );
  }
  return domains;
};

const allowedDomains = parseAllowedDomains(
  configHelpers.getCommonConfig()?.authAllowedDomains
);

// Same rules as axios 0.19 `isAbsoluteURL` + `combineURLs`
// (`core/buildFullPath`), so the host checked here is the host the request
// goes to
const isAbsoluteURL = (url: string) => /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url);
const combineURLs = (baseURL: string, relativeURL: string) =>
  relativeURL
    ? `${baseURL.replace(/\/+$/, '')}/${relativeURL.replace(/^\/+/, '')}`
    : baseURL;

const resolveUrl = (url = '', baseURL?: string) => {
  const fullPath =
    baseURL && !isAbsoluteURL(url) ? combineURLs(baseURL, url) : url;
  try {
    return new URL(fullPath, window.location.href);
  } catch {
    return null;
  }
};

const isTokenAllowed = (
  url?: string,
  baseURL?: string,
  domains: string[] = allowedDomains
) => {
  const target = resolveUrl(url, baseURL);
  if (!target || target.protocol !== 'https:' || target.port !== '') {
    return false;
  }
  const hostname = target.hostname.toLowerCase();
  return domains.some(
    domain => hostname === domain || hostname.endsWith(`.${domain}`)
  );
};

/** Headers for a request made outside the axios clients (`fetch`, plain `axios`, upload components). */
const getAuthorizationHeader = (
  url: string,
  token?: string,
  domains: string[] = allowedDomains
): Record<string, string> =>
  token && isTokenAllowed(url, undefined, domains)
    ? { [AUTHORIZATION]: toBearer(token) }
    : {};

/** Request interceptor body: removes any `Authorization` set elsewhere, then adds it back only for an allowed host. */
const applyAuthorization = (
  config: AxiosRequestConfig,
  token?: string,
  domains: string[] = allowedDomains
) => {
  const headers = config.headers || {};
  [headers, headers.common, config.method && headers[config.method]].forEach(
    group => {
      if (group) delete group[AUTHORIZATION];
    }
  );
  if (token && isTokenAllowed(config.url, config.baseURL, domains)) {
    headers[AUTHORIZATION] = toBearer(token);
  }
  config.headers = headers;
  return config;
};

export default {
  toBearer,
  normalizeDomain,
  parseAllowedDomains,
  isTokenAllowed,
  getAuthorizationHeader,
  applyAuthorization,
};
